import React from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import CurrentClientContext from '../../../providers/CurrentClientProvider'
import ClientFeatureFlagService, { AI_CHAT_BOT_FEATURE_FLAG } from '../../../services/ClientFeatureFlagService'
import { buildV3ClientDashboardPath } from '../../../services/ClientDashboardV3'
import V3IndieChatFull from '../../../components/orbit/v3/V3IndieChatFull'
import type { OrbitChatViewHandoff } from '../../../hooks/useOrbitChat'

type IndieAiLocationState = {
  initialPrompt?: unknown;
  source?: unknown;
  orbitChatHandoff?: OrbitChatViewHandoff;
} | null

export default function ClientDashboardV3IndieAi() {
  const location = useLocation()
  const navigate = useNavigate()
  const currentClientContext = CurrentClientContext.useClient() as any
  const client = currentClientContext?.client || {}
  const initialConversationId = new URLSearchParams(location.search).get('conversation_id') || undefined
  const locationState = location.state as IndieAiLocationState
  const initialPrompt = typeof locationState?.initialPrompt === 'string'
    ? locationState.initialPrompt
    : undefined
  const initialHandoff = locationState?.orbitChatHandoff
  const launchSource = locationState?.source
  const isApprovedDashboardLaunch = launchSource === 'otb-kpi' || launchSource === 'dashboard-ask-indie'
  const resolvedClientCode = client?.ClientCode || ''
  const clientId = client?.Id ?? client?.id ?? 0
  const featureFlagsMap = ClientFeatureFlagService.normalizeFeatureFlagsMap(client)
  const hasEmbeddedAiChatFlag = Object.prototype.hasOwnProperty.call(featureFlagsMap, AI_CHAT_BOT_FEATURE_FLAG)
  const embeddedAiChatValue = featureFlagsMap[AI_CHAT_BOT_FEATURE_FLAG]
  const [isAiChatBotEnabled, setIsAiChatBotEnabled] = React.useState<boolean | null>(
    hasEmbeddedAiChatFlag ? Boolean(embeddedAiChatValue) : null,
  )

  const handleConversationIdChange = React.useCallback((conversationId?: string) => {
    const nextSearch = new URLSearchParams(location.search)
    const currentConversationId = nextSearch.get('conversation_id') || undefined

    if (conversationId) {
      if (currentConversationId === conversationId) {
        return
      }
      nextSearch.set('conversation_id', conversationId)
    } else {
      if (!currentConversationId) {
        return
      }
      nextSearch.delete('conversation_id')
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch.toString() ? `?${nextSearch.toString()}` : '',
      },
      { replace: true, state: location.state },
    )
  }, [location.pathname, location.search, location.state, navigate])

  React.useEffect(() => {
    let cancelled = false
    if (!clientId) {
      setIsAiChatBotEnabled(null)
      return () => { cancelled = true }
    }
    if (hasEmbeddedAiChatFlag) {
      setIsAiChatBotEnabled(Boolean(embeddedAiChatValue))
      return () => { cancelled = true }
    }

    setIsAiChatBotEnabled(null)
    ClientFeatureFlagService.getFlags(clientId)
      .then((flags) => {
        if (!cancelled) setIsAiChatBotEnabled(Boolean(flags?.[AI_CHAT_BOT_FEATURE_FLAG]))
      })
      .catch(() => {
        if (!cancelled) setIsAiChatBotEnabled(false)
      })

    return () => { cancelled = true }
  }, [clientId, embeddedAiChatValue, hasEmbeddedAiChatFlag])

  if (resolvedClientCode && isAiChatBotEnabled === null && !isApprovedDashboardLaunch) {
    return <div className="flex h-[calc(100vh-80px)] items-center justify-center bg-[#F8F7FB] text-sm text-[#667085]">Loading Ask Indie…</div>
  }

  if (resolvedClientCode && isAiChatBotEnabled === false && !isApprovedDashboardLaunch) {
    return <Navigate to={buildV3ClientDashboardPath(resolvedClientCode)} replace />
  }

  return (
    <div className="h-[calc(100vh-80px)] min-h-[720px] min-w-0 overflow-hidden bg-[#F8F7FB]">
      <V3IndieChatFull
        clientId={clientId}
        client={{ Name: client?.Name, ClientCode: resolvedClientCode }}
        initialConversationId={initialConversationId}
        initialPrompt={initialPrompt}
        initialHandoff={initialHandoff}
        onConversationIdChange={handleConversationIdChange}
      />
    </div>
  )
}

